import { api, unwrap } from "./httpClient";
import type {
  Account,
  AccountTransaction,
  AccountTransfer,
  TransferAccountsPayload,
  WithdrawalRequestPayload,
  TradingPerformanceGranularity,
  TradingPerformanceResult,
} from "../types/api";

/**
 * Récupère tous les comptes de l'utilisateur connecté
 */
export const getUserAccounts = async (): Promise<Account[]> => {
  return unwrap(api<Account[]>({ method: "GET", url: "/accounts" }));
};

/**
 * Effectue un transfert entre compte principal et trading
 */
export const transferBetweenAccounts = async (
  payload: TransferAccountsPayload
): Promise<AccountTransfer> => {
  return unwrap(
    api<AccountTransfer>({
      method: "POST",
      url: "/accounts/transfer",
      data: payload,
    })
  );
};

/**
 * Récupère les transactions d'un compte
 */
export const getAccountTransactions = async (
  accountId: number,
  limit: number = 50,
  offset: number = 0
): Promise<AccountTransaction[]> => {
  return unwrap(
    api<AccountTransaction[]>({
      method: "GET",
      url: `/accounts/${accountId}/transactions`,
      params: { limit, offset },
    })
  );
};

/**
 * Performances de trading agrégées par période (heure, jour, mois, année).
 * Optionnellement filtré par symbole. Retourne rows + liste des symboles.
 */
export const getTradingPerformance = async (
  granularity: TradingPerformanceGranularity,
  from?: string,
  to?: string,
  symbol?: string
): Promise<TradingPerformanceResult> => {
  const params: Record<string, string> = { granularity };
  if (from) params.from = from;
  if (to) params.to = to;
  if (symbol) params.symbol = symbol;
  return unwrap(
    api<TradingPerformanceResult>({
      method: "GET",
      url: "/accounts/trading-performance",
      params,
    })
  );
};

/**
 * Récupère les transferts de l'utilisateur
 */
export const getUserTransfers = async (
  limit: number = 50,
  offset: number = 0
): Promise<AccountTransfer[]> => {
  return unwrap(
    api<AccountTransfer[]>({
      method: "GET",
      url: "/accounts/transfers",
      params: { limit, offset },
    })
  );
};

/**
 * Demande un retrait depuis le compte principal
 */
export const requestWithdrawal = async (
  payload: WithdrawalRequestPayload
): Promise<AccountTransaction> => {
  return unwrap(
    api<AccountTransaction>({
      method: "POST",
      url: "/accounts/withdraw",
      data: payload,
      // Timeout spécifique plus long pour les retraits (2 minutes)
      timeout: 120_000,
    })
  );
};
